import { Link } from "react-router-dom";
import type { AnimalAcquisitionType, AnimalDetail, AnimalLineageRef } from "../types";
import { formatAge } from "../utils/formatAge";
import { formatCurrency } from "../utils/formatCurrency";
import { formatDate } from "../utils/formatDate";

type Props = {
  animal: AnimalDetail;
};

// Render orquestado de todas las secciones de info read-only.
// El gateo por rol vive en el page (RoleGate alrededor de AnimalAccountingInfo).
// Acá adentro, AccountingSection hace una segunda guard contra firstCalvingAt
// null (animal pre-primer-parto: cualquier rol ve undefined porque la
// depreciación no arrancó).
export function AnimalInfoSections({ animal }: Props) {
  return (
    <div className="space-y-6">
      <GeneralSection animal={animal} />
      <AcquisitionSection animal={animal} />
      <LineageSection mother={animal.mother} father={animal.father} />
      {animal.sex === "female" && animal.firstCalvingAt && (
        <ReproductiveSection firstCalvingAt={animal.firstCalvingAt} />
      )}
    </div>
  );
}

// Sección contable. Renderizada desde AnimalDetailPage envuelta en RoleGate
// (admin/owner/viewer). El check interno cubre el caso de novilla joven que
// todavía no parió: aunque el rol permita ver, no hay datos.
export function AnimalAccountingSection({ animal }: Props) {
  if (animal.firstCalvingAt == null) return null;
  if (animal.usefulLifeMonths == null && animal.residualValue == null) return null;

  return (
    <Section title="Contable">
      <Field label="Vida útil estimada" value={formatUsefulLife(animal.usefulLifeMonths)} />
      <Field label="Valor residual" value={formatCurrency(animal.residualValue)} mono />
    </Section>
  );
}

export function AnimalLocationSection({ animal }: Props) {
  return (
    <Section title="Ubicación">
      <Field
        label="Lote actual"
        value={animal.currentLotName ?? <span className="text-app-ink-3">Sin lote</span>}
      />
    </Section>
  );
}

// ─── Sub-secciones ────────────────────────────────────────────────────────────

function GeneralSection({ animal }: Props) {
  const ageSuffix = animal.birthDateIsEstimated ? " · estimada" : "";
  return (
    <Section title="Información general">
      <Field label="Raza" value={animal.breed ?? "—"} />
      <Field
        label="Fecha de nacimiento"
        value={
          <span>
            <span className="font-mono">{formatDate(animal.birthDate)}</span>
            <span className="text-app-ink-3">
              {" · "}
              {formatAge(animal.birthDate)}
              {ageSuffix}
            </span>
          </span>
        }
      />
    </Section>
  );
}

const ACQUISITION_LABEL: Record<AnimalAcquisitionType, string> = {
  born: "Nacido en finca",
  purchased: "Compra",
  imported: "Importación",
};

function AcquisitionSection({ animal }: Props) {
  const showAcquiredDate = animal.acquiredAt !== animal.birthDate;
  return (
    <Section title="Adquisición">
      <Field label="Tipo" value={ACQUISITION_LABEL[animal.acquisitionType]} />
      {showAcquiredDate && (
        <Field label="Fecha de adquisición" value={formatDate(animal.acquiredAt)} mono />
      )}
      {animal.initialCost !== undefined && (
        <Field label="Costo inicial" value={formatCurrency(animal.initialCost)} mono />
      )}
    </Section>
  );
}

function LineageSection({
  mother,
  father,
}: {
  mother: AnimalLineageRef | null;
  father: AnimalLineageRef | null;
}) {
  return (
    <Section title="Linaje">
      <Field label="Madre" value={<LineageValue ref={mother} />} />
      <Field label="Padre" value={<LineageValue ref={father} />} />
    </Section>
  );
}

function LineageValue({ ref }: { ref: AnimalLineageRef | null }) {
  if (!ref) return <span className="text-app-ink-3">—</span>;
  return (
    <Link
      to={`/app/hato/${ref.id}`}
      className="text-app-ink-1 underline-offset-2 hover:text-app-emerald hover:underline"
    >
      <span className="font-mono">{ref.tag}</span>
      {ref.name && <span className="text-app-ink-2">{` · ${ref.name}`}</span>}
    </Link>
  );
}

function ReproductiveSection({ firstCalvingAt }: { firstCalvingAt: string }) {
  return (
    <Section title="Reproductivo">
      <Field label="Fecha de primer parto" value={formatDate(firstCalvingAt)} mono />
    </Section>
  );
}

// ─── Primitives ───────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-4 md:p-5">
      <h2 className="mb-3 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        {title}
      </h2>
      <dl className="space-y-2">{children}</dl>
    </section>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
      <dt className="shrink-0 text-[12px] text-app-ink-3 sm:w-44">{label}</dt>
      <dd className={`text-[14px] text-app-ink-1 ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}

function formatUsefulLife(months: number | null | undefined): string {
  if (months == null) return "—";
  if (months < 12) return `${months} ${months === 1 ? "mes" : "meses"}`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yearsLabel = `${years} ${years === 1 ? "año" : "años"}`;
  if (rem === 0) return yearsLabel;
  return `${yearsLabel}, ${rem} ${rem === 1 ? "mes" : "meses"}`;
}
